import React, { useEffect, useRef, useState } from 'react';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { FaBed, FaBath, FaCar, FaRulerCombined, FaHome, FaStar, FaTimes, FaChevronLeft, FaChevronRight, FaBan, FaUndo, FaHandPointer } from 'react-icons/fa';

const GOOGLE_MAPS_API_KEY = 'AIzaSyCtn44ogwI_q0TGiuwC1X_sOXYijuEdr5M';
const MAP_ID = '1ae451cd191c1a06';

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

// Compact, lower-case price for the pin itself: "$1.1m", "$800k". Computed from
// the raw numeric price when present so it's always shortened; falls back to the
// pre-formatted display_price (which may be a range / "Contact agent").
const formatShortPrice = (property) => {
  const v = Number(property.price);
  if (property.price != null && !isNaN(v) && v > 0) {
    if (v >= 1000000) return `$${(v / 1000000).toFixed(1).replace(/\.0$/, '')}m`;
    if (v >= 1000) return `$${Math.round(v / 1000)}k`;
    return `$${v.toLocaleString()}`;
  }
  return property.display_price || null;
};

const landText = (land_area) => {
  if (land_area == null) return null;
  if (typeof land_area === 'object') return `${land_area.size}${land_area.unit || 'm²'}`;
  return `${land_area}m²`;
};

// Pull the fullest photo gallery a comparable carries, whatever shape it's in:
// a photo_urls array, a `media` field (JSON string or array of {url}), an images
// array, else just the single main photo. Keeps the carousel populated rather
// than falling back to one static image.
const galleryFor = (p) => {
  if (Array.isArray(p.photo_urls) && p.photo_urls.length) return p.photo_urls.filter(Boolean);
  let media = p.media;
  if (typeof media === 'string') { try { media = JSON.parse(media); } catch { media = null; } }
  if (Array.isArray(media) && media.length) {
    const urls = media.map(m => (typeof m === 'string' ? m : (m && (m.url || m.src)))).filter(Boolean);
    if (urls.length) return urls;
  }
  if (Array.isArray(p.images) && p.images.length) return p.images.filter(Boolean);
  return [p.photo_url_main].filter(Boolean);
};

// Fixed media height for the preview card's carousel. An explicit height (rather
// than aspect-ratio) guarantees the gallery is visible inside the absolutely
// positioned card on every browser.
const MEDIA_HEIGHT = 190;

const scoreLabels = ['', 'Low', 'Fair', 'Good', 'Strong', 'Excellent'];
const scoreColors = ['', '#ef4444', '#f59e0b', '#22c55e', '#10b981', '#059669'];
const scoreBgColors = ['', '#fef2f2', '#fffbeb', '#f0fdf4', '#ecfdf5', '#ecfdf5'];

// ─── Lazy, swipeable image carousel for the preview card ─────────────────────
// Mounts only when a pin is selected (the card renders on demand) and loads each
// slide lazily as it's first viewed, so opening the map never fetches every
// comparable's gallery up front — which would otherwise hammer the image host.
const PreviewCarousel = ({ images, alt }) => {
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState(() => new Set([0]));
  const startX = useRef(null);

  if (!images || images.length === 0) {
    return (
      <div style={{ width: '100%', height: MEDIA_HEIGHT, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <FaHome size={34} color="#cbd1d8" />
      </div>
    );
  }

  const go = (n) => {
    const next = Math.max(0, Math.min(images.length - 1, n));
    setIdx(next);
    setLoaded(prev => (prev.has(next) ? prev : new Set(prev).add(next)));
  };
  const onTouchStart = (e) => { startX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (startX.current == null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (Math.abs(dx) > 40) go(idx + (dx < 0 ? 1 : -1));
    startX.current = null;
  };
  const navBtn = (side) => ({
    position: 'absolute', top: '50%', [side]: 8, transform: 'translateY(-50%)',
    width: 28, height: 28, borderRadius: '50%', border: 'none', cursor: 'pointer',
    background: 'rgba(255,255,255,0.94)', color: '#1a1a1a', padding: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(0,0,0,0.22)',
  });

  return (
    <div
      style={{ position: 'relative', width: '100%', height: MEDIA_HEIGHT, background: '#f3f4f6', overflow: 'hidden' }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div style={{ display: 'flex', height: '100%', width: `${images.length * 100}%`, transform: `translateX(-${idx * (100 / images.length)}%)`, transition: 'transform 0.28s ease' }}>
        {images.map((url, i) => (
          <div key={i} style={{ width: `${100 / images.length}%`, height: '100%' }}>
            {loaded.has(i) && (
              <img
                src={url}
                alt={`${alt || 'Property'} ${i + 1}`}
                loading="lazy"
                draggable={false}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                onError={e => { e.target.style.visibility = 'hidden'; }}
              />
            )}
          </div>
        ))}
      </div>
      {images.length > 1 && (
        <>
          {idx > 0 && <button onClick={e => { e.stopPropagation(); go(idx - 1); }} style={navBtn('left')}><FaChevronLeft size={12} /></button>}
          {idx < images.length - 1 && <button onClick={e => { e.stopPropagation(); go(idx + 1); }} style={navBtn('right')}><FaChevronRight size={12} /></button>}
          <div style={{ position: 'absolute', bottom: 8, right: 10, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11, fontWeight: 600, borderRadius: 999, padding: '2px 8px' }}>
            {idx + 1} / {images.length}
          </div>
        </>
      )}
    </div>
  );
};

// ─── The redesigned preview card (buyers-agent style) ────────────────────────
// A clean white card docked at the bottom of the map: image carousel up top with
// a Sold/Rented badge, then price, address, specs and the AI match score.
const MapPreviewCard = ({ property, accentColor, onClose, isExcluded, disableExclude, onToggleExclude }) => {
  const soldDate = property.sold_date || (property.sold_timestamp ? formatDate(property.sold_timestamp) : null);
  const filterDate = property.filter_date ? formatDate(property.filter_date) : null;
  const dateStr = soldDate || filterDate;
  const dateLabel = property.filter_date_label || (property.listing_type === 'Rent' ? 'Rented' : 'Sold');
  const price = property.display_price || (property.price ? `$${Number(property.price).toLocaleString()}` : null);
  const images = galleryFor(property);
  const land = landText(property.land_area);
  const score = property.ai_analysis?.score;
  const spec = { display: 'inline-flex', alignItems: 'center', gap: 4 };

  return (
    <div
      onClick={e => e.stopPropagation()}
      style={{
        position: 'absolute', left: 12, right: 12, bottom: 12, margin: '0 auto', maxWidth: 360, zIndex: 5,
        background: '#fff', border: '1px solid #ececec', borderRadius: 16, overflow: 'hidden',
        boxShadow: '0 14px 36px rgba(0,0,0,0.24)', animation: 'ppmapcard-in 0.22s ease-out',
      }}
    >
      <div style={{ position: 'relative' }}>
        <PreviewCarousel images={images} alt={property.display_address || property.address} />
        <span style={{
          position: 'absolute', top: 8, left: 8, zIndex: 2, fontSize: 11, fontWeight: 600,
          letterSpacing: '0.05em', textTransform: 'uppercase', color: '#fff',
          background: dateLabel === 'Rented' ? '#0d9488' : '#dc2626',
          borderRadius: 999, padding: '4px 11px', boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
        }}>{dateLabel}</span>
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute', top: 8, right: 8, zIndex: 2, width: 28, height: 28, borderRadius: '50%',
            border: 'none', cursor: 'pointer', background: 'rgba(0,0,0,0.55)', color: '#fff', padding: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <FaTimes size={12} />
        </button>
      </div>

      <div style={{ padding: '12px 14px 14px', display: 'flex', flexDirection: 'column', gap: 7 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
          {price && <div style={{ fontSize: 17, fontWeight: 700, color: '#111', lineHeight: 1.2 }}>{price}</div>}
          {dateStr && (
            <span style={{ fontSize: 11, fontWeight: 500, color: '#6b7280', background: '#f3f4f6', borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {dateLabel} {dateStr}
            </span>
          )}
        </div>

        <div style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.35 }}>
          {property.display_address || property.address || 'Address withheld'}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12.5, color: '#6b7280' }}>
          {property.bedrooms != null && <span style={spec}><FaBed size={12} /> {property.bedrooms}</span>}
          {property.bathrooms != null && <span style={spec}><FaBath size={12} /> {property.bathrooms}</span>}
          {property.carspaces != null && <span style={spec}><FaCar size={12} /> {property.carspaces}</span>}
          {land && <span style={spec}><FaRulerCombined size={11} /> {land}</span>}
          {property.property_type_display && <span style={{ ...spec, color: '#9ca3af' }}>{property.property_type_display}</span>}
        </div>

        {score != null && (() => {
          const s = Math.max(1, Math.min(5, Math.round(score)));
          return (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start', backgroundColor: scoreBgColors[s], padding: '4px 10px', borderRadius: 999, border: `1px solid ${scoreColors[s]}22`, marginTop: 1 }}>
              <div style={{ display: 'flex', gap: 2 }}>
                {[1, 2, 3, 4, 5].map(i => <FaStar key={i} size={9} style={{ color: i <= s ? scoreColors[s] : '#e5e7eb' }} />)}
              </div>
              <span style={{ fontSize: 11, fontWeight: 600, color: scoreColors[s] }}>{scoreLabels[s]} match</span>
            </div>
          );
        })()}

        {/* Exclude / include from the estimate — same action as the list view. */}
        {onToggleExclude && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); if (!disableExclude) onToggleExclude(property); }}
            disabled={disableExclude}
            title={disableExclude ? 'Keep at least one comparable' : isExcluded ? 'Include in estimate' : 'Exclude from estimate'}
            style={{
              marginTop: 4, alignSelf: 'flex-start',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: isExcluded ? '#f0fdf4' : '#fff',
              border: `1px solid ${isExcluded ? '#16a34a' : '#e5e7eb'}`,
              color: isExcluded ? '#16a34a' : '#6b7280',
              borderRadius: 999, padding: '6px 14px', fontSize: '0.78rem', fontWeight: 600,
              cursor: disableExclude ? 'not-allowed' : 'pointer', opacity: disableExclude ? 0.45 : 1,
            }}
          >
            {isExcluded ? <><FaUndo size={10} /> Include in estimate</> : <><FaBan size={10} /> Exclude from estimate</>}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Subject Property Marker ─────────────────────────────────────────────────
// A small dot marking the property being valued — deliberately tiny so the
// comparable price pins stay the focus. The native title shows "Your Property".
const SubjectMarker = ({ position }) => (
  <AdvancedMarker position={position} zIndex={100} title="Your Property">
    <div style={{
      width: 16, height: 16, borderRadius: '50%',
      background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
      border: '2px solid #fff', boxShadow: '0 1px 5px rgba(0,0,0,0.35)',
    }} />
  </AdvancedMarker>
);

// ─── Comparable Property Marker (price pill) ─────────────────────────────────
const ComparableMarker = ({ position, property, accentColor, isActive, onClick }) => {
  const priceLabel = formatShortPrice(property);
  return (
    <AdvancedMarker position={position} onClick={onClick} zIndex={isActive ? 3 : 1}>
      {/* The pin is the price itself — a compact pill carrying the shortened
          price (e.g. "$1.1m"). It fills with the accent colour while selected. */}
      <div style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{
          background: isActive ? accentColor : '#fff',
          color: isActive ? '#fff' : '#1a1a1a',
          border: `2px solid ${accentColor}`,
          borderRadius: 999, padding: '4px 11px',
          fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap',
          boxShadow: isActive ? '0 4px 14px rgba(0,0,0,0.28)' : '0 2px 10px rgba(0,0,0,0.18)',
          transform: isActive ? 'scale(1.06)' : 'none', transition: 'transform 0.12s ease',
        }}>
          {priceLabel || <FaHome size={12} />}
        </div>
        <div style={{
          width: 0, height: 0, marginTop: -1,
          borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
          borderTop: `6px solid ${isActive ? accentColor : '#fff'}`,
          filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.12))',
        }} />
      </div>
    </AdvancedMarker>
  );
};

// ─── Main Map Component ──────────────────────────────────────────────────────
const AppraisalMap = ({ comparables, subjectProperty, accentColor, excludedIds, onToggleExclude }) => {
  const [activeId, setActiveId] = useState(null);
  const [center, setCenter] = useState(null);
  const [zoom, setZoom] = useState(null);

  // A stable signature of *which* properties are shown, not the array's
  // identity. Parents build the `comparables` prop inline (e.g. an
  // `.filter().map()` in render), so its reference changes on every unrelated
  // re-render — a page scroll, a sibling state update. Depending on the array
  // reference made these effects re-run on those renders, and the second one
  // reset the open preview card — which is why the card randomly vanished while
  // swiping through a listing's photos. Keying on the ids instead means we only
  // react when the set genuinely changes.
  const comparablesKey = (comparables || []).map((c, i) => c.id ?? i).join('|');
  const subjectKey = subjectProperty ? `${subjectProperty.latitude},${subjectProperty.longitude}` : '';

  useEffect(() => {
    if (!comparables || comparables.length === 0) return;
    const allPoints = [...comparables];
    if (subjectProperty?.latitude && subjectProperty?.longitude) {
      allPoints.push(subjectProperty);
    }
    const total = allPoints.reduce((acc, p) => ({ lat: acc.lat + p.latitude, lng: acc.lng + p.longitude }), { lat: 0, lng: 0 });
    setCenter({ lat: total.lat / allPoints.length, lng: total.lng / allPoints.length });
    setZoom(15);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparablesKey, subjectKey]);

  // Drop the open card only when the comparable set actually changes underneath
  // it — not merely because the parent handed us a new array instance.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setActiveId(null); }, [comparablesKey]);

  if (!comparables || comparables.length === 0 || !center || !zoom) {
    return (
      <div style={{ height: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
        No comparables to display on map.
      </div>
    );
  }

  const active = activeId != null ? comparables[activeId] : null;
  const excluded = excludedIds || new Set();
  const includedCount = comparables.filter(c => !excluded.has(c.id)).length;
  const activeExcluded = active ? excluded.has(active.id) : false;
  // Mirror the list's rule: never let the buyer exclude the last comparable.
  const activeDisableExclude = !activeExcluded && includedCount <= 1;

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <style>{`@keyframes ppmapcard-in { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      <div style={{ position: 'relative', width: '100%', height: 500 }}>
        <Map
          mapId={MAP_ID}
          defaultCenter={center}
          defaultZoom={zoom}
          gestureHandling="greedy"
          clickableIcons={false}
          disableDefaultUI={false}
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={false}
          onClick={() => setActiveId(null)}
          style={{ width: '100%', height: '100%' }}
        >
          {comparables.map((comp, idx) => (
            <ComparableMarker
              key={comp.id || idx}
              position={{ lat: comp.latitude, lng: comp.longitude }}
              property={comp}
              accentColor={accentColor}
              isActive={activeId === idx}
              onClick={() => setActiveId(idx)}
            />
          ))}

          {subjectProperty?.latitude && subjectProperty?.longitude && (
            <SubjectMarker position={{ lat: subjectProperty.latitude, lng: subjectProperty.longitude }} />
          )}
        </Map>

        {/* Hint that the price pins open a property card — shown until one is opened. */}
        {!active && (
          <div style={{
            position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 4,
            display: 'inline-flex', alignItems: 'center', gap: 7, pointerEvents: 'none',
            background: 'rgba(26,26,26,0.82)', color: '#fff', borderRadius: 999,
            padding: '7px 14px', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
            boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
          }}>
            <FaHandPointer size={12} /> Tap a price pin to view the property
          </div>
        )}

        {active && (
          <MapPreviewCard
            key={active.id || activeId}
            property={active}
            accentColor={accentColor}
            isExcluded={activeExcluded}
            disableExclude={activeDisableExclude}
            onToggleExclude={onToggleExclude}
            onClose={() => setActiveId(null)}
          />
        )}
      </div>
    </APIProvider>
  );
};

export default AppraisalMap;
